<template>
    <div ref="rootElement" class="np-comments__list">
        <PerfectScrollbar :key="componentKey" ref="commentScrollContainer">
            <div v-for="date in sortedDates" :key="date">
                <h3 class="np-font-m np-f-regular text-center pt-4 pb-4 mb-2">{{ date }}</h3>
                <div class="np-comments">
                    <div class="comment-wrapper" v-for="comment in groupedComments[date]" :key="comment.id">
                        <div class="comment mb-2" :class="{'my-comment' : this.activeUserId == comment.user_id}">
                            <div class="ps-2 pe-2">
                                <CobaltProfile 
                                    :firstName="comment.user.name" 
                                    :lastName="comment.user.last_name" 
                                    :short="true"
                                />
                            </div>
                            <div class="comment-content p-2">
                                <div class="replyToComment" v-if="comment.reply">
                                    <span>{{ comment.reply.user.name }}</span> 
                                    {{ comment.reply.body }}
                                </div>
                                <div class="np-group np-group-column">
                                    {{ comment.body }}
                                    <div class="np-group justify-content-between">
                                        <div class="comment-reactions"></div>
                                        <span class="np-f-light">{{ localeTime(comment.date_create) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="comment-actions">
                            <button class="btn btn-base me-1" @click="replyComment(comment)">
                                <img src="@/assets/images/dashboard/base-reply.svg">
                            </button>
                            <div class="dropdown">
                                <button class="btn btn-link btn-sm text-body-tertiary" :id="'commentDropdownMenuLink' + comment.id" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                                    <img src="@/assets/images/dashboard/more-vertical.svg">
                                </button>
                                <ul class="dropdown-menu" :aria-labelledby="'commentDropdownMenuLink' + comment.id">
                                    <li class="dropdown-item">
                                        <button type="button" class="btn btn-link btn-sm me-2 text-body-tertiary" @click="replyComment(comment)">
                                            {{ $t("UI.task.commentReplay") }}
                                        </button>
                                    </li>
                                    <li class="dropdown-item">
                                        <button type="button" class="btn btn-link btn-sm me-2 text-body-tertiary" @click="deleteComment(comment.id)">
                                            {{ $t("UI.task.commentDelete") }}
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>  
                </div>
            </div>
        </PerfectScrollbar>
    </div>
    <div class="np-comments__footer">
        <div class="replyPanel" v-if="replyToComment">
            <div class="replyToComment">
                <span>{{ replyToComment.user.name}}</span>
                {{ replyToComment.body }}
            </div>
            <button type="button" class="cobalt-toolbar__icon-btn" @click="replyToComment = null">
                <img src="@/assets/images/dashboard/base-close-dark.svg" />
            </button>
        </div>
        <div class="np-group">
            <input type="text" :placeholder="$t('UI.task.commentWrite')" class="np-comment__input flex-grow-1" v-model="comment" ref="commentTextInput" @keypress.enter="sendComment"></input>
            <button class="np-btn np-btn-primary-light np-p-custom-0" :class="comment.length == 0 ? 'disabled' : ''" type="button" @click="sendComment">
                <svg><use href="@/assets/images/icons/send2.svg#icon"></use></svg>
            </button>
        </div>
    </div>
</template>
<script>
    import { mapActions } from 'vuex'
    import { localeTime } from '@/utils/format'
    import { confirm } from "@/utils/confirmation.js"

    export default {
        name: 'CommentsList',
        props: {
            taskId: {
                type: Number,
                default: 0,
                required: true
            },
            isSubtask: {
                type: Boolean,
                default: false,
                required: true
            },
            activeUserId: {
                type: Number,
                default: 0,
                required: true
            },
            comments: {
                type: Array,
                default: [],
                required: true
            }
        },
        data() {
            return {
                comment: '',
                replyToComment: null,
                isVisible: false,
                componentKey: 0,
                observer: null                        
            }
        },
        computed: {
            groupedComments() {
                return this.comments?.reduce((groups, comment) => {
                    const date = comment.date_create * 1000;
                    const fDate = new Date(date).toLocaleDateString('ru-RU')

                    if (!groups[fDate]) {
                        groups[fDate] = []
                    }
                    groups[fDate].push(comment)
                    return groups
                }, {})
            },
            sortedDates() {
                if (!this.groupedComments) {
                    return []
                }
                return Object.keys(this.groupedComments).sort((a, b) => a - b)
            }                 
        },
        mounted() {
            this.initVisibilityObserver()

            this.$nextTick(() => {
                if (this.$refs.commentScrollContainer) {
                    this.commentsScrollToBottom()
                }
            })            
        },
        beforeDestroy() {
            if (this.observer) {
                this.observer.disconnect()
            }
        },  
        watch: {
            comments: {
                handler(newVal, oldVal) {
                    if (newVal.length !== oldVal?.length) {
                        this.$nextTick(() => {
                            this.handleChange()
                        })
                    }
                },
                deep: true,
                immediate: true
            }
        },            
        methods: {
            ...mapActions('board', ['addTaskComment', 'deleteTaskComment']),
            localeTime,

            initVisibilityObserver() {
                this.$nextTick(() => {
                    this.observer = new IntersectionObserver((entries) => {
                        entries.forEach(entry => {
                        if (entry.isIntersecting !== this.isVisible) {
                            this.isVisible = entry.isIntersecting
                            this.handleChange()
                        }
                        })
                    }, {
                        threshold: 0.1
                    })

                    if (this.$refs.rootElement) {
                        this.observer.observe(this.$refs.rootElement)
                    }
                })
            },
            async sendComment() {
                if (this.comment.length == 0) {
                    return
                }

                let commentData = {
                    'body': this.comment,
                    'task_id': this.taskId,
                    'isSubtask': this.isSubtask
                }

                if (this.replyToComment) {
                    commentData['replyTo'] = this.replyToComment.id
                }

                let result = await this.addTaskComment(commentData)

                this.replyToComment = null

                if (result) {
                    this.comment = ''
                }

                this.refreshParent()
            }, 
            async deleteComment(id) {
                const confirmed = await confirm("Подтверждение", "Вы уверены, что хотите удалить?", this.$t("UI.ok"), this.$t("UI.cancel"))
                if (!confirmed) return

                let commentData = {
                    'id': id,
                    'isSubtask': this.isSubtask
                }
                let result = await this.deleteTaskComment(commentData)
                this.refreshParent()
            },
            replyComment(comment) {
                this.replyToComment = comment
                this.$refs.commentTextInput.focus()
            },
            refreshParent() {
                this.$emit('refresh')
            },
            commentsScrollToBottom() {
                const container = this.$refs.commentScrollContainer
                if (!container) return

                if (container.$el) {
                    container.$el.scrollTop = container.$el.scrollHeight
                }
            },
            handleChange() {
                if (this.isVisible) {
                    this.componentKey += 1
                }
                this.$nextTick(() => {
                    this.commentsScrollToBottom()
                })
            }                      
        }
    }
</script>
  
<style scoped>

</style>